import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Check, Star, Zap, Crown, Building2, ArrowRight,
  Package, Users, Plug, MessageSquare, Shield, Gift, Calculator, ChevronDown
} from "lucide-react";

const Pricing = () => {
  const [billingCycle, setBillingCycle] = useState('mensal');
  const [showCalculator, setShowCalculator] = useState(false);
  const [calculatorData, setCalculatorData] = useState({
    marketplace: 'mercado_livre',
    precoVenda: 100,
    custoCompra: 60,
    frete: 15
  });

  const plans = [
    {
      id: 'iniciante',
      name: 'Iniciante',
      description: 'Para começar a vender',
      icon: Package,
      priceMonthly: 49.90,
      priceYearly: 39,
      color: 'from-cyan-400 to-blue-500',
      bgColor: 'bg-gradient-to-br from-cyan-50 to-blue-100',
      paymentUrl: 'https://pay.kiwify.com.br/SzzAeex',
      features: [
        '5 Produtos',
        '2 Marketplaces',
        'IA Básica',
        'Relatórios simples',
        'Suporte por email'
      ]
    },
    {
      id: 'universal',
      name: 'Universal',
      description: 'Tudo incluído',
      icon: Star,
      priceMonthly: 99.90,
      priceYearly: 79,
      color: 'from-orange-400 to-pink-500',
      bgColor: 'bg-gradient-to-br from-orange-50 to-pink-100',
      popular: true,
      paymentUrl: 'https://pay.kiwify.com.br/KrnYDec',
      features: [
        'Produtos ilimitados',
        'Todos marketplaces',
        'IA Avançada 24/7',
        'Relatórios completos',
        'Suporte prioritário',
        'Calculadora de margem',
        'Tendências em tempo real'
      ]
    },
    {
      id: 'empresa',
      name: 'Diamante Brasil',
      description: 'Para grandes operações',
      icon: Building2,
      priceMonthly: 299.90,
      priceYearly: 239,
      color: 'from-purple-400 to-violet-500',
      bgColor: 'bg-gradient-to-br from-purple-50 to-violet-100',
      paymentUrl: 'https://pay.kiwify.com.br/zTBa1uS',
      features: [
        'Tudo do Universal',
        'Múltiplos usuários',
        'API personalizada',
        'Gerente dedicado',
        'Treinamento exclusivo',
        'SLA garantido',
        'Integrações custom'
      ]
    }
  ];

  // TODOS os marketplaces com suas taxas
  const marketplaces = [
    { id: 'mercado_livre', name: 'Mercado Livre', taxa: 16, logo: '🟡' },
    { id: 'shopee', name: 'Shopee', taxa: 14, logo: '🟠' },
    { id: 'amazon', name: 'Amazon', taxa: 15, logo: '📦' },
    { id: 'magalu', name: 'Magazine Luiza', taxa: 16, logo: '🔵' },
    { id: 'americanas', name: 'Americanas', taxa: 16, logo: '🔴' },
    { id: 'casas_bahia', name: 'Casas Bahia', taxa: 16, logo: '🟡' },
    { id: 'shopify', name: 'Shopify', taxa: 2, logo: '🟢' },
    { id: 'woocommerce', name: 'WooCommerce', taxa: 0, logo: '🟣' },
    { id: 'nuvemshop', name: 'Nuvemshop', taxa: 2.49, logo: '☁️' },
    { id: 'shein', name: 'Shein', taxa: 20, logo: '⚫' },
    { id: 'aliexpress', name: 'AliExpress', taxa: 8, logo: '🔶' },
    { id: 'enjoei', name: 'Enjoei', taxa: 20, logo: '💖' },
    { id: 'olist', name: 'Olist', taxa: 15, logo: '🟦' },
    { id: 'madeira_madeira', name: 'MadeiraMadeira', taxa: 18, logo: '🪵' },
    { id: 'carrefour', name: 'Carrefour', taxa: 16, logo: '🔵' },
    { id: 'extra', name: 'Extra', taxa: 16, logo: '🔴' },
    { id: 'ponto', name: 'Ponto', taxa: 16, logo: '🟡' },
    { id: 'netshoes', name: 'Netshoes', taxa: 15, logo: '👟' },
    { id: 'dafiti', name: 'Dafiti', taxa: 25, logo: '👗' },
    { id: 'centauro', name: 'Centauro', taxa: 16, logo: '⭐' },
  ];

  const getPrice = (plan) => {
    return billingCycle === 'mensal' ? plan.priceMonthly : plan.priceYearly;
  };

  const calculateMargin = () => {
    const marketplace = marketplaces.find(m => m.id === calculatorData.marketplace);
    const taxaMarketplace = marketplace?.taxa || 0;
    const taxaClickHub = 2; // Taxa fixa ClickHub
    
    const precoVenda = parseFloat(calculatorData.precoVenda) || 0;
    const custoCompra = parseFloat(calculatorData.custoCompra) || 0;
    const frete = parseFloat(calculatorData.frete) || 0;
    
    const valorTaxaMarketplace = (precoVenda * taxaMarketplace) / 100;
    const valorTaxaClickHub = (precoVenda * taxaClickHub) / 100;
    const custoTotal = custoCompra + frete + valorTaxaMarketplace + valorTaxaClickHub;
    const lucroLiquido = precoVenda - custoTotal;
    const margemLucro = precoVenda > 0 ? (lucroLiquido / precoVenda) * 100 : 0;
    
    return {
      taxaMarketplace,
      taxaClickHub,
      valorTaxaMarketplace: valorTaxaMarketplace.toFixed(2),
      valorTaxaClickHub: valorTaxaClickHub.toFixed(2),
      custoTotal: custoTotal.toFixed(2),
      lucroLiquido: lucroLiquido.toFixed(2),
      margemLucro: margemLucro.toFixed(1)
    };
  };

  const margin = calculateMargin();

  return (
    <div className="flex flex-col h-full bg-gradient-to-b from-gray-900 to-gray-950">
      <div className="flex-1">
        <div className="p-4 lg:p-6">
          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">
              Escolha o Plano <span className="bg-gradient-to-r from-orange-400 to-pink-400 bg-clip-text text-transparent">Perfeito</span> para Você
            </h1>
            <p className="text-gray-300 text-sm">Escale suas vendas com nossa plataforma inteligente. 7 dias de teste grátis!</p>
          </div>

          {/* Billing Toggle */}
          <div className="flex justify-center mb-6">
            <div className="bg-gray-800/80 p-1.5 rounded-xl flex gap-1">
              <Button
                size="sm"
                onClick={() => setBillingCycle('mensal')}
                className={`h-9 px-6 ${billingCycle === 'mensal' ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white' : 'bg-transparent text-gray-300 hover:text-white'}`}
              >
                Mensal
              </Button>
              <Button
                size="sm"
                onClick={() => setBillingCycle('anual')}
                className={`h-9 px-6 ${billingCycle === 'anual' ? 'bg-gradient-to-r from-orange-500 to-pink-500 text-white' : 'bg-transparent text-gray-300 hover:text-white'}`}
              >
                Anual <span className="ml-2 text-green-400 text-xs font-bold">-20%</span>
              </Button>
            </div>
          </div>

          {/* Plans Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-5xl mx-auto mb-8">
            {plans.map((plan) => (
              <Card
                key={plan.id}
                className={`relative bg-gray-800/40 backdrop-blur-sm border-gray-700/50 p-5 hover:border-gray-600 transition-all ${plan.popular ? 'border-orange-500/50 ring-2 ring-orange-500/20 scale-105' : ''}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-gradient-to-r from-orange-500 to-pink-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-lg">
                      <Star className="w-3 h-3" />MAIS POPULAR
                    </span>
                  </div>
                )}

                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${plan.color} flex items-center justify-center mb-4 shadow-lg`}>
                  <plan.icon className="w-6 h-6 text-white" />
                </div>

                <h3 className="text-white text-xl font-bold">{plan.name}</h3>
                <p className="text-gray-400 text-sm mb-4">{plan.description}</p>

                <div className="mb-4">
                  <span className="text-white text-4xl font-bold">R$ {getPrice(plan).toFixed(2)}</span>
                  <span className="text-gray-400 text-sm">/ mês</span>
                </div>

                <Button 
                  className={`w-full mb-4 h-10 font-semibold bg-gradient-to-r ${plan.color} hover:opacity-90 transition-opacity`}
                  onClick={() => window.open(plan.paymentUrl, '_blank')}
                  data-testid={`plan-${plan.id}-btn`}
                >
                  {plan.popular ? 'Começar Agora' : 'Selecionar'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>

                <ul className="space-y-2">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-gray-300">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>

          {/* Calculadora de Margem - TODOS os Marketplaces */}
          <Card className="bg-gradient-to-br from-gray-800/60 to-gray-900/60 border-gray-700/50 p-5 max-w-4xl mx-auto mb-6">
            <div 
              className="flex items-center justify-between cursor-pointer"
              onClick={() => setShowCalculator(!showCalculator)}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-500 rounded-xl flex items-center justify-center">
                  <Calculator className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-white text-lg font-bold">Calculadora de Margem</h3>
                  <p className="text-gray-400 text-sm">Calcule seu lucro em TODOS os {marketplaces.length} marketplaces</p>
                </div>
              </div>
              <ChevronDown className={`w-6 h-6 text-gray-400 transition-transform ${showCalculator ? 'rotate-180' : ''}`} />
            </div>

            {showCalculator && (
              <div className="mt-5 pt-5 border-t border-gray-700">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Formulário */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Marketplace</label>
                      <select
                        value={calculatorData.marketplace}
                        onChange={(e) => setCalculatorData({...calculatorData, marketplace: e.target.value})}
                        className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2.5"
                      >
                        {marketplaces.map((mp) => (
                          <option key={mp.id} value={mp.id}>
                            {mp.logo} {mp.name} (Taxa: {mp.taxa}%)
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Preço de Venda (R$)</label>
                      <Input
                        type="number"
                        value={calculatorData.precoVenda}
                        onChange={(e) => setCalculatorData({...calculatorData, precoVenda: e.target.value})}
                        className="bg-gray-800 border-gray-700 text-white h-10"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Custo do Produto (R$)</label>
                      <Input
                        type="number"
                        value={calculatorData.custoCompra}
                        onChange={(e) => setCalculatorData({...calculatorData, custoCompra: e.target.value})}
                        className="bg-gray-800 border-gray-700 text-white h-10"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Custo do Frete (R$)</label>
                      <Input
                        type="number"
                        value={calculatorData.frete}
                        onChange={(e) => setCalculatorData({...calculatorData, frete: e.target.value})}
                        className="bg-gray-800 border-gray-700 text-white h-10"
                      />
                    </div>
                  </div>

                  {/* Resultado */}
                  <div className="bg-gray-900/50 rounded-xl p-4">
                    <h4 className="text-white font-semibold mb-4">Resultado da Simulação</h4>
                    
                    <div className="space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Preço de Venda:</span>
                        <span className="text-white font-semibold">R$ {parseFloat(calculatorData.precoVenda || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">(-) Custo Produto:</span>
                        <span className="text-red-400">R$ {parseFloat(calculatorData.custoCompra || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">(-) Frete:</span>
                        <span className="text-red-400">R$ {parseFloat(calculatorData.frete || 0).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">(-) Taxa Marketplace ({margin.taxaMarketplace}%):</span>
                        <span className="text-red-400">R$ {margin.valorTaxaMarketplace}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">(-) Taxa ClickHub ({margin.taxaClickHub}%):</span>
                        <span className="text-red-400">R$ {margin.valorTaxaClickHub}</span>
                      </div>
                      
                      <div className="border-t border-gray-700 pt-3 mt-3">
                        <div className="flex justify-between">
                          <span className="text-gray-300 font-semibold">Lucro Líquido:</span>
                          <span className={`text-xl font-bold ${parseFloat(margin.lucroLiquido) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            R$ {margin.lucroLiquido}
                          </span>
                        </div>
                        <div className="flex justify-between mt-2">
                          <span className="text-gray-300 font-semibold">Margem de Lucro:</span>
                          <span className={`text-xl font-bold ${parseFloat(margin.margemLucro) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                            {margin.margemLucro}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Card>

          {/* Features */}
          <div className="max-w-4xl mx-auto">
            <h3 className="text-white text-base font-bold text-center mb-4">Todos os planos incluem</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {[
                { icon: Zap, text: 'IA Inteligente', color: 'text-yellow-400' },
                { icon: Shield, text: 'Dados seguros', color: 'text-blue-400' },
                { icon: MessageSquare, text: 'Suporte dedicado', color: 'text-green-400' },
                { icon: Gift, text: '7 dias grátis', color: 'text-pink-400' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 p-3 bg-gray-800/40 rounded-lg border border-gray-700/50">
                  <item.icon className={`w-5 h-5 ${item.color}`} />
                  <span className="text-white text-sm">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
